<template>
  <div>
    <h1 class="settings-title">
      Front
    </h1>
    <div class="settings-form">
      <div v-if="step === 0">
        <b-form-group>
          <a
            :class="{loading: saving}"
            :disabled="saving"
            :href="get_front_oauth_link()"
            class="btn-round btn-save btn-primary">
            Authorize Front
          </a>
        </b-form-group>
        <div class="description-box">
          *To connect AI Number with Front, please click the authorize button.
        </div>
      </div>
      <div v-if="step === 1">
        <h2 class="settings-subtitle">
          Api Secret
        </h2>
        <b-form-group>
          <label class="settings-label">
            <span>API Secret</span>
            <icon
              id="api-secret-description"
              name="info-circle"
              class="info-label"/>
            <b-tooltip
              :delay="{ show: 0, hide: 1500 }"
              target="api-secret-description"
              placement="right"
              offset="0, 5"
              no-fade>
              You can find your Front API secret in <b class="text-success">Settings > Plugins & API > API</b>.
              If you're already logged in to your Front web app simply click
              <a
                target="_blank"
                href="https://app.frontapp.com/settings/tools/api"><b>here</b></a> to visit the page.
            </b-tooltip>
          </label>
          <b-input
            v-model="apiSecret"
            v-validate="'required'"
            name="api_secret"
            data-vv-as="API Secret"
            placeholder="API Secret"
            type="text"/>
          <span
            v-if="errors.has('api_secret')"
            class="validation-error">{{ errors.first('api_secret') }}</span>
          <span
            v-if="validationErrors.apiSecret"
            class="validation-error">{{ validationErrors.apiSecret }}</span>
        </b-form-group>
        <b-form-group>
          <label
            class="settings-label"
            style="display:none;">
            <span>Token</span>
          </label>
          <b-input
            v-model="jwt"
            v-validate="'required'"
            name="jwt"
            data-vv-as="Token"
            placeholder="Token"
            type="text"
            readonly="readonly"
            style="display:none;" />
          <span
            v-if="errors.has('jwt')"
            class="validation-error">{{ errors.first('jwt') }}</span>
          <span
            v-if="validationErrors.jwt"
            class="validation-error">{{ validationErrors.jwt }}</span>
        </b-form-group>
        <div class="description-box">
          *Secret key will be encrypted and stored in our database securely.
        </div>
        <div class="clearfix"/>
        <div
          v-if="errorMessage"
          class="error-message">
          {{ errorMessage }}
        </div>
        <b-button
          :class="{loading: saving}"
          :disabled="saving"
          class="btn-round btn-save"
          @click="goToSecondStep">
          <span v-if="!saving">Next</span>
          <div class="loader"><div class="loading-circle"/></div>
        </b-button>
        <router-link
          :to="{ name: 'IntegrationList' }"
          class="btn btn-round btn-cancel ml-2">
          Cancel
        </router-link>
      </div>
      <div v-if="step === 2">
        <h2 class="settings-subtitle">
          Create Inbox
        </h2>
        <b-form-group>
          <label class="settings-label">
            Team
          </label>
          <b-select
            v-model="teamId"
            v-validate="'required'"
            :plain="true"
            :options="frontTeams"
            name="team_id"
            data-vv-as="Front Team"
            value-field="id"
            text-field="name"
            class="settings-select"/>
          <span
            v-if="errors.has('team_id')"
            class="validation-error">{{ errors.first('team_id') }}</span>
          <span
            v-if="validationErrors.teamId"
            class="validation-error">{{ validationErrors.teamId }}</span>
        </b-form-group>
        <b-form-group>
          <label class="settings-label">
            Inbox Name
          </label>
          <b-input
            v-model="inboxName"
            v-validate="'required'"
            name="inbox_name"
            data-vv-as="Inbox Name"
            placeholder="Inbox Name"
            type="text"/>
          <span
            v-if="errors.has('inbox_name')"
            class="validation-error">{{ errors.first('inbox_name') }}</span>
          <span
            v-if="validationErrors.inboxName"
            class="validation-error">{{ validationErrors.inboxName }}</span>
        </b-form-group>
        <div class="clearfix"/>
        <div
          v-if="errorMessage"
          class="error-message">
          {{ errorMessage }}
        </div>
        <b-button
          :class="{loading: saving}"
          :disabled="saving"
          class="btn-round btn-save"
          @click="goToThirdStep">
          <span v-if="!saving">Next</span>
          <div class="loader"><div class="loading-circle"/></div>
        </b-button>
        <router-link
          :to="{ name: 'IntegrationList' }"
          class="btn btn-round btn-cancel ml-2">
          Cancel
        </router-link>
      </div>
      <div v-if="step === 3">
        <h2 class="settings-subtitle">
          Add Plugin
        </h2>
        <b-form-group>
          <label class="settings-label">
            <span>Plugin Link</span>
            <icon
              id="plugin-description"
              name="info-circle"
              class="info-label"/>
            <b-tooltip
              :delay="{ show: 0, hide: 1500 }"
              target="plugin-description"
              placement="right"
              offset="0, 5"
              no-fade>
              To install AI Number Plugin that will allow you to easily send Template Messages to WhatApp
              contacts in your Front app, go to <b class="text-success">Settings > Plugins & API > Plugins</b>.
              If you're already logged in to your Front web app simply click
              <a
                target="_blank"
                href="https://app.frontapp.com/settings/tools/plugins"><b>here</b></a> to visit the page.<br>
              Once you visit the <b class="text-success">Front Plugins</b> page click
              <b class="text-success">Add a plugin</b> button and in the field Endpoint Copy and Paste the url
              you can find in the field below.
              Once the plugin is installed, Front will generate an Auth Secret key for your plugin,
              that you need to Copy and Paste into the Plugin Auth Secret field in your
              <b class="text-success">AI Number dashboard</b>.
            </b-tooltip>
          </label>
          <CopyInput :value="pluginLink"/>
        </b-form-group>
        <b-form-group>
          <label class="settings-label">
            Plugin Auth Secret
          </label>
          <b-input
            v-model="pluginKey"
            v-validate="'required'"
            name="plugin_key"
            data-vv-as="Plugin Auth Secret"
            placeholder="Plugin Auth Secret"
            type="text"/>
          <span
            v-if="errors.has('plugin_key')"
            class="validation-error">{{ errors.first('plugin_key') }}</span>
          <span
            v-if="validationErrors.pluginKey"
            class="validation-error">{{ validationErrors.pluginKey }}</span>
        </b-form-group>
        <div class="clearfix"/>
        <div
          v-if="errorMessage"
          class="error-message">
          {{ errorMessage }}
        </div>
        <b-button
          :class="{loading: saving}"
          :disabled="saving"
          class="btn-round btn-save"
          @click="makeFrontIntegration">
          <span v-if="!saving">Save</span>
          <div class="loader"><div class="loading-circle"/></div>
        </b-button>
        <router-link
          :to="{ name: 'IntegrationList' }"
          class="btn btn-round btn-cancel ml-2">
          Cancel
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import CopyInput from '../../../common/CopyInput';
import setValidationErrors from '@/helpers/validationErrors';

export default {
  name: 'FrontIntegrationSettings',
  components: {
    CopyInput,
  },
  data() {
    return {
      step: this.set_step(),
      apiSecret: '',
      jwt: this.populate_jwt(),
      teamId: '',
      pluginKey: '',
      inboxName: 'Number WhatsApp',
      validationErrors: {},
      saving: false,
      linkCopied: false,
      linkError: false,
      errorMessage: false,
    };
  },
  computed: {
    ...mapGetters({
      enabledIntegrations: 'integration/integrations',
      appKey: 'merchant/appKey',
      frontTeams: 'integration/frontTeams',
      frontTeamsLoaded: 'integration/frontTeamsLoaded',
      frontIntegrated: 'integration/frontIntegrated',
      frontToken: 'integration/frontToken',
      frontTokenLoaded: 'integration/frontTokenLoaded',
      apiError: 'integration/apiError',
    }),
    pluginLink() {
      return `${this.$env.VUE_APP_PLUGIN_URL}/front/index.html?m_id=${this.$route.params.merchant_id}&app_key=${this.appKey.public_key}`;
    },

  },
  watch: {
    frontTeamsLoaded(loaded) {
      if (loaded) {
        this.saving = false;
        this.step = 2;
        this.teamId = this.frontTeams[0].id;
      }
    },
    frontIntegrated(integrated) {
      if (integrated) {
        this.saving = false;
        this.$router.push({ name: 'IntegrationList' });
      }
    },
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.validationErrors = setValidationErrors(error.data.errors);
      } else {
        this.errorMessage = error.data.message;
      }
    },
    frontTokenLoaded(loaded) {
      if (loaded) {
        this.step = 1;
        this.jwt = this.frontToken.access_token;
      }
    },
  },
  mounted() {
    this.fetchAppKey(this.$route.params.merchant_id);
    if (!this.enabledIntegrations.length) {
      this.fetchIntegrations(this.$route.params.merchant_id);
    }

    if (this.frontIntegrated) {
      this.$router.push({ name: 'IntegrationList' });
    }

    if (!this.frontTokenLoaded) {
      if (this.$route.query.step === 'token') {
        this.fetchFrontToken({
          merchant_id: this.$route.params.merchant_id,
          data: {
            code: this.$route.query.code,
            redirect_uri: this.$env.VUE_APP_FRONT_REDIRECT_URI,
          },
        });
      }
    }
  },
  methods: {
    ...mapActions({
      fetchAppKey: 'merchant/fetchAppKey',
      fetchIntegrations: 'integration/fetchIntegrations',
      fetchFrontTeamList: 'integration/fetchFrontTeamList',
      fetchFrontToken: 'integration/fetchFrontToken',
      integrateFront: 'integration/integrateFront',
    }),
    populate_jwt() {
      if (this.$route.query.code !== undefined) {
        return this.$route.query.code;
      }
      return '';
    },
    set_step() {
      if (this.$route.query.step !== undefined) {
        return parseInt(this.$route.query.step, 10);
      }
      return 0;
    },
    get_front_oauth_link() {
      return this.$env.VUE_APP_FRONT_OAUTH_URL.replace('{{merchant_id}}', this.$route.params.merchant_id);
    },
    goToSecondStep() {
      this.validationErrors = {};
      this.errorMessage = '';

      this.$validator.validate().then((valid) => {
        if (valid) {
          this.saving = true;
          this.fetchFrontTeamList({
            merchant_id: this.$route.params.merchant_id,
            data: {
              jwt: this.jwt,
            },
          });
        }
      });
    },
    goToThirdStep() {
      this.validationErrors = {};
      this.errorMessage = '';

      this.$validator.validate().then((valid) => {
        if (valid) {
          this.step = 3;
        }
      });
    },
    makeFrontIntegration() {
      this.validationErrors = {};
      this.errorMessage = '';

      this.$validator.validate().then((valid) => {
        if (valid) {
          this.saving = true;
          this.integrateFront({
            merchant_id: this.$route.params.merchant_id,
            data: {
              api_secret: this.apiSecret,
              jwt: this.jwt,
              team_id: this.teamId,
              inbox_name: this.inboxName,
              plugin_key: this.pluginKey,
            },
          });
        }
      });
    },
  },
};
</script>
